import { notification } from '@pankod/refine-antd';
import axios, { AxiosError } from 'axios';

import { API_BASE_URL } from 'configs';
import { AUTH_TOKEN_KEY } from 'services/auth';
import { StorageService } from 'services/storage';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Set true to skip global error notification for this request */
    skipErrorNotification?: boolean;
  }
}

export const httpClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

httpClient.interceptors.request.use(config => {
  const token = StorageService.get<string>(AUTH_TOKEN_KEY);

  if (token && config.headers) {
    // eslint-disable-next-line no-param-reassign
    config.headers.authorization = `Bearer ${token}`;
  }

  return config;
});

type ApiErrorPayload = {
  code?: string;
  message?: string;
  chain?: string[];
};

function getNestedError(data: unknown): ApiErrorPayload | null {
  if (!data || typeof data !== 'object') return null;
  const body = data as Record<string, unknown>;
  const err = body.error;
  if (!err || typeof err !== 'object') return null;
  const e = err as Record<string, unknown>;
  const payload: ApiErrorPayload = {};
  if (typeof e.code === 'string') payload.code = e.code;
  if (typeof e.message === 'string') payload.message = e.message;
  if (Array.isArray(e.chain)) {
    payload.chain = e.chain.filter(
      (item): item is string => typeof item === 'string'
    );
  }
  if (
    payload.message ||
    payload.code ||
    (payload.chain && payload.chain.length)
  ) {
    return payload;
  }
  return null;
}

function getErrorMessage(error: AxiosError<unknown>): string {
  const { response, message } = error;
  const data = response?.data;

  const nested = getNestedError(data);
  if (nested?.code) {
    return nested.code;
  }
  if (nested?.message) {
    return nested.message;
  }

  if (data && typeof data === 'object') {
    const body = data as Record<string, unknown>;
    if (typeof body.message === 'string') return body.message;
    if (Array.isArray(body.message)) return body.message.join(', ');
    if (typeof body.error === 'string') return body.error;
  }

  if (typeof data === 'string' && data.trim()) return data;

  if (response?.statusText) return response.statusText;

  return message || 'Request failed';
}

function getErrorDescription(error: AxiosError<unknown>): string | undefined {
  const data = error.response?.data;
  if (!data || typeof data !== 'object') return undefined;
  const body = data as Record<string, unknown>;

  const nested = getNestedError(data);
  if (nested) {
    const parts: string[] = [];
    if (nested.code && nested.message) {
      parts.push(nested.message);
    }
    if (nested.chain?.length) {
      parts.push(nested.chain.join('\n'));
    }
    if (parts.length) {
      return parts.join('\n\n');
    }
  }

  if (body.errors != null) {
    try {
      return JSON.stringify(body.errors);
    } catch {
      return String(body.errors);
    }
  }
  return undefined;
}

httpClient.interceptors.response.use(
  res => res,
  (error: AxiosError<unknown>) => {
    const { config } = error;

    if (config?.skipErrorNotification) {
      return Promise.reject(error);
    }

    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    const title = getErrorMessage(error);
    const description = getErrorDescription(error);

    notification.error({
      message: title,
      ...(description ? { description } : {}),
      duration: 4.5
    });

    return Promise.reject(error);
  }
);
